import { type FC } from 'react'

import { F0Box, F0Button, F0Dialog, F0Heading, F0Text } from '@factorialco/f0-react'
import { Marketplace } from '@factorialco/f0-react/icons/app'

import i18n from 'modules/core/lib/i18n'
import type { CartItem } from 'modules/it_management/hooks/device_catalog/types'

import { CartItemRow } from './CartItemRow'

export type MarketplaceCartSidepanelProps = {
  isOpen: boolean
  items: CartItem[]
  subtotal: number
  total: number
  itemCount: number
  onClose: () => void
  onUpdateQuantity: (variantId: string, offerId: string, quantity: number) => void
  onRemoveItem: (variantId: string, offerId: string) => void
  onCheckout: () => void
  isCheckingOut: boolean
}

function formatEuroPrice(amount: number): string {
  return `${amount.toLocaleString('de-DE', { minimumFractionDigits: 0 })} €`
}

export const MarketplaceCartSidepanel: FC<MarketplaceCartSidepanelProps> = ({
  isOpen,
  items,
  subtotal,
  total,
  itemCount,
  onClose,
  onUpdateQuantity,
  onRemoveItem,
  onCheckout,
  isCheckingOut,
}) => {
  if (!isOpen) return null

  const isEmpty = items.length === 0

  const subtotalLabel =
    itemCount === 1
      ? i18n.t('it_management.device_catalog.cart.subtotal', { count: itemCount })
      : i18n.t('it_management.device_catalog.cart.subtotal_other', { count: itemCount })

  return (
    <F0Dialog
      isOpen
      onClose={onClose}
      title={i18n.t('it_management.device_catalog.cart.title')}
      position="right"
      disableContentPadding
    >
      {/* Fixed height so flex pushes footer to bottom; only the list scrolls (dialog wraps us in ScrollArea) */}
      <div
        className="flex min-h-0 flex-col overflow-hidden"
        style={{
          height: 'calc(100vh - 6rem)',
          maxHeight: 'calc(100vh - 6rem)',
        }}
      >
        {/* Cart items area — only this section scrolls */}
        <div
          data-testid="cart-items-scroll-area"
          className="flex min-h-0 flex-1 flex-col overflow-y-auto px-2 pt-4 pb-4"
        >
          {isEmpty ? (
            <div className="flex flex-col items-center justify-center px-2 py-12">
              <F0Heading
                variant="heading"
                content={i18n.t('it_management.device_catalog.cart.empty_title')}
              />
              <F0Text
                variant="description"
                content={i18n.t('it_management.device_catalog.cart.empty_description')}
              />
            </div>
          ) : (
            items.map((item) => (
              <div key={`${item.variantId}-${item.offerId}`} className="shrink-0 px-2">
                <CartItemRow
                  item={item}
                  onUpdateQuantity={onUpdateQuantity}
                  onRemove={onRemoveItem}
                />
              </div>
            ))
          )}
        </div>

        {/* Footer: pricing summary + buttons — always visible at bottom */}
        {!isEmpty && (
          <div className="flex shrink-0 flex-col pb-2">
            <F0Box display="flex" flexDirection="column" gap="md">
              {/* Divider — full width */}
              <div>
                <F0Box
                  height="1px"
                  border="default"
                  borderColor="secondary"
                  borderRadius="md"
                ></F0Box>
              </div>

              {/* Content with padding */}
              <div className="px-4">
                {/* Subtotal */}
                <F0Box
                  display="flex"
                  flexDirection="row"
                  justifyContent="between"
                  alignItems="center"
                >
                  <F0Text variant="description" content={subtotalLabel} />
                  <F0Text variant="label" content={formatEuroPrice(subtotal)} />
                </F0Box>

                {/* Estimated VAT */}
                <F0Box
                  display="flex"
                  flexDirection="row"
                  justifyContent="between"
                  alignItems="center"
                  paddingTop="md"
                >
                  <F0Text
                    variant="description"
                    content={i18n.t('it_management.device_catalog.cart.estimated_vat')}
                  />
                  <F0Text
                    variant="description"
                    content={i18n.t('it_management.device_catalog.cart.vat_calculated')}
                  />
                </F0Box>
              </div>

              {/* Divider — full width */}
              <div>
                <F0Box
                  height="1px"
                  border="default"
                  borderColor="secondary"
                  borderRadius="md"
                ></F0Box>
              </div>

              {/* Content with padding */}
              <div className="px-4">
                {/* Total */}
                <F0Box
                  display="flex"
                  flexDirection="row"
                  justifyContent="between"
                  alignItems="center"
                >
                  <F0Text
                    variant="label"
                    content={i18n.t('it_management.device_catalog.cart.total')}
                  />
                  <F0Heading variant="heading" content={formatEuroPrice(total)} />
                </F0Box>

                {/* Action buttons */}
                <F0Box
                  display="flex"
                  flexDirection="row"
                  gap="md"
                  justifyContent="end"
                  paddingTop="lg"
                >
                  <F0Button
                    variant="outline"
                    label={i18n.t('it_management.device_catalog.cart.cancel')}
                    onClick={onClose}
                  />
                  <F0Button
                    label={i18n.t('it_management.device_catalog.cart.proceed_to_checkout')}
                    icon={Marketplace}
                    onClick={onCheckout}
                    disabled={isCheckingOut}
                  />
                </F0Box>
              </div>
            </F0Box>
          </div>
        )}
      </div>
    </F0Dialog>
  )
}
